import * as React from 'react';
import {
    AdminUserSearchResult, PaymentDetails, Plan, PlanPackageCriterion,
} from 'fs-api-client';
import { FETCHING_MESSAGE, SAVING_MESSAGE } from '../../utils/constants';
import { renderWaitingForSuccessElement } from '../../../webportal/components/common/Icons';
import { BillingView } from './BillingView';
import { BillingPlanViewModel } from './BillingPlanViewModel';
import { BillingPlan, BillingPlanState, TierProp } from './BillingPlanModel';
import { UserPaymentDetails } from '../user/UserPaymentDetails';
import { ErrorRenderer } from '../common/ErrorRenderer';
import { Providers } from 'portal-shared-lib';

interface BillingDetailsProps {
    user: AdminUserSearchResult;
    securityScopes: string[];
    totalPaid: number;
    handleDisableTopBarStatus: () => void;
    setParentCallback: (callback: any) => void;
    disableAddPlanButton: () => void;
}

interface BillingForecast {
    amountDue: number;
    amountDueForecast: number;
    currency: string;
    trialDays: number;
    start: number;
    end: number;
}

enum ViewState {
    NotInitialized = 'NotInitialized',
    Ready = 'Ready',
    Saving = 'Saving',
    Loading = 'Loading'
}

interface BillingDetailsState {
    hasChanges: boolean;
    hasError: boolean;
    errorMessage: string;
    viewState: ViewState;
    billingPlan?: BillingPlan;
    billingForecast?: BillingForecast;
    paymentDetails: PaymentDetails | undefined;
}

const BILLING_WRITE_SCOPE = 'billingWrite';
const MASTER_SCOPE = 'master';

export class Billing extends React.Component<BillingDetailsProps, BillingDetailsState> {
    private billingPlanViewModel: BillingPlanViewModel;
    private timeoutId: any = null;

    constructor(props: BillingDetailsProps) {
        super(props);

        this.onBillingPlanLoaded = this.onBillingPlanLoaded.bind(this);
        this.onBillingPlanLoadError = this.onBillingPlanLoadError.bind(this);
        this.onBillingPlanUpdated = this.onBillingPlanUpdated.bind(this);
        this.onBillingPlanSaved = this.onBillingPlanSaved.bind(this);
        this.onBillingPlanSaveError = this.onBillingPlanSaveError.bind(this);
        this.onStartEditing = this.onStartEditing.bind(this);
        this.onStopEditing = this.onStopEditing.bind(this);

        this.billingPlanViewModel = new BillingPlanViewModel(
            this.props.user,
            this.onBillingPlanLoaded,
            this.onBillingPlanLoadError,
            this.onBillingPlanUpdated,
            this.onBillingPlanSaved,
            this.onBillingPlanSaveError
        );

        this.addNewPlanPackage = this.addNewPlanPackage.bind(this);
        this.removeErrorComponent = this.removeErrorComponent.bind(this);

        this.props.setParentCallback(this.addNewPlanPackage);
        this.state = {
            hasChanges: false,
            hasError: false,
            viewState: ViewState.NotInitialized,
            errorMessage: '',
            paymentDetails: undefined
        };

        this.changePlanPackStorageOwner = this.changePlanPackStorageOwner.bind(this);
        this.changePlanPackStorageProvider = this.changePlanPackStorageProvider.bind(this);
        this.changePlanPackSubscriptionType = this.changePlanPackSubscriptionType.bind(this);
        this.addProvider = this.addProvider.bind(this);
        this.changeProvider = this.changeProvider.bind(this);
        this.removeProvider = this.removeProvider.bind(this);
        this.removePlanPackage = this.removePlanPackage.bind(this);
        this.onPlanTierModified = this.onPlanTierModified.bind(this);
        this.onPlanTierRemoved = this.onPlanTierRemoved.bind(this);
        this.onPlanDimensionChanged = this.onPlanDimensionChanged.bind(this);
        this.onPlanTierAdded = this.onPlanTierAdded.bind(this);
        this.applyChanges = this.applyChanges.bind(this);
        this.undoChanges = this.undoChanges.bind(this);
        this.onPlanRemoved = this.onPlanRemoved.bind(this);
        this.onSubscriptionTypeAdded = this.onSubscriptionTypeAdded.bind(this);
        this.onSubscriptionTypeRemoved = this.onSubscriptionTypeRemoved.bind(this);
        this.onBillingPlanReverted = this.onBillingPlanReverted.bind(this);
        this.hasBillingWritePermissions = this.hasBillingWritePermissions.bind(this);
    }

    async componentDidMount() {
        await this.fetchBillingPlan(this.props.user.id);
    }

    render() {
        return (
            // TODO Render the header here, no need to wait for both foreast and plan
            <React.Fragment>
                {this.isReady() && this.userHasPaymentDetails() && this.renderUserPaymentDetails()}
                {this.isReady() && this.state.billingPlan && this.state.billingForecast &&
                    <BillingView
                        billingPlan={this.state.billingPlan}
                        totalPaid={this.props.totalPaid}
                        hasChanges={this.state.hasChanges}
                        currency={this.state.billingForecast.currency}
                        handleDisableTopBarStatus={this.props.handleDisableTopBarStatus}
                        onAddPlanPackageClicked={this.addNewPlanPackage}
                        onPlanDimensionChanged={this.onPlanDimensionChanged}
                        onPlanPackStorageOwnerChange={this.changePlanPackStorageOwner}
                        onPlanPackStorageProviderChange={this.changePlanPackStorageProvider}
                        onPlanPackSubscriptionTypeChange={this.changePlanPackSubscriptionType}
                        onProviderAdded={this.addProvider}
                        onProviderChange={this.changeProvider}
                        onProviderRemoved={this.removeProvider}
                        onPlanPackageRemoved={this.removePlanPackage}
                        onPlanTierAdded={this.onPlanTierAdded}
                        onPlanTierModified={this.onPlanTierModified}
                        onPlanTierRemoved={this.onPlanTierRemoved}
                        onSubscriptionTypeAdded={this.onSubscriptionTypeAdded}
                        onSubscriptionTypeRemoved={this.onSubscriptionTypeRemoved}
                        onSaveClicked={this.applyChanges}
                        billingForecast={this.state.billingForecast}
                        onStartEditing={this.onStartEditing}
                        onStopEditing={this.onStopEditing}
                        onUndoChangesClicked={this.undoChanges}
                        onPlanRemoved={this.onPlanRemoved}
                        onBillingPlanReverted={this.onBillingPlanReverted}
                        errorMessage={this.state.errorMessage}
                        canEdit={this.hasBillingWritePermissions()}
                    />}
                {this.state.hasError && <ErrorRenderer errorMessage={this.state.errorMessage}/>}
                {this.isLoading() && renderWaitingForSuccessElement(FETCHING_MESSAGE)}
                {this.isSaving() && renderWaitingForSuccessElement(SAVING_MESSAGE)}
            </React.Fragment>
        );
    }

    componentWillUnmount(): void {
        this.clearErrorTimeout();
    }

    private hasBillingWritePermissions() {
        return this.props.securityScopes.includes(MASTER_SCOPE) ||
            this.props.securityScopes.includes(BILLING_WRITE_SCOPE);
    }

    private renderUserPaymentDetails(): JSX.Element | null {
        if (!this.userHasPaymentDetails()) {
            return null;
        }

        return (
            <React.Fragment>
                <div className="row listItem">
                    <div>
                        <h2>Billing Details: </h2>
                    </div>
                </div>
                <div className="row listItem">
                    <UserPaymentDetails paymentDetails={this.state.paymentDetails}/>
                </div>
            </React.Fragment>
        );
    }

    private userHasPaymentDetails(): boolean {
        const paymentDetails = this.state.paymentDetails;
        if (!paymentDetails) {
            return false;
        }

        const billingAddress = paymentDetails.billingAddress;
        return !!(paymentDetails.name ||
            paymentDetails.description ||
            (billingAddress &&
                (billingAddress.country ||
                    billingAddress.city ||
                    billingAddress.postalCode ||
                    billingAddress.state ||
                    billingAddress.line1 ||
                    billingAddress.line2))
        );
    }

    private onBillingPlanReverted() {
        this.setState({ viewState: ViewState.Saving });
        this.billingPlanViewModel.revertBillingPlan();
    }

    private onSubscriptionTypeAdded(planPackId: string, subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum) {
        this.billingPlanViewModel.onSubscriptionTypeAdded(planPackId, subscriptionType);
    }

    private onSubscriptionTypeRemoved(planPackId: string, criterionIdx: number) {
        this.billingPlanViewModel.onSubscriptionTypeRemoved(planPackId, criterionIdx);
    }

    private undoChanges() {
        this.billingPlanViewModel.undoChanges();
    }

    private onStartEditing() {
        this.billingPlanViewModel.createCheckPoint();
    }

    private onStopEditing(applyChanges: boolean) {
        if (applyChanges) {
            this.billingPlanViewModel.removeCheckPoint();
        } else {
            this.billingPlanViewModel.restoreCheckPoint();
        }
    }

    private isReady() {
        return this.state.viewState === ViewState.Ready;
    }

    private isLoading() {
        return this.state.viewState === ViewState.Loading;
    }

    private isSaving() {
        return this.state.viewState === ViewState.Saving;
    }

    private async fetchBillingPlan(userId: string): Promise<void> {
        this.setState({ viewState: ViewState.Loading });
        this.billingPlanViewModel.fetchBillingPlan(userId);
    }

    private onBillingPlanLoaded(billingPlan: BillingPlan, forecast: any, paymentDetails: PaymentDetails) {
        this.setState(
            {
                billingPlan,
                viewState: ViewState.Ready,
                billingForecast: forecast,
                errorMessage: '',
                hasError: false,
                paymentDetails
            }
        );
        this.clearErrorTimeout();
    }

    private onBillingPlanUpdated(billingPlanState: BillingPlanState) {
        this.setState(
            {
                billingPlan: billingPlanState.billingPlan,
                hasChanges: billingPlanState.hasChanges
            }
        );
    }

    private setError(errMessage: string) {
        this.setState(
            {
                errorMessage: errMessage,
                hasError: true,
                viewState: ViewState.Ready
            });

        this.clearErrorTimeout();

        this.timeoutId = setTimeout(
            () => {
                this.setState({ hasError: false, errorMessage: '' });
            },
            5000);
    }

    private clearErrorTimeout() {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
        }
    }

    private onBillingPlanLoadError(err: any) {
        if (!err.body.BillingPlan) {
            this.setState(
                {
                    errorMessage: 'This user has no billing plans',
                    viewState: ViewState.NotInitialized,
                    hasError: true,
                }
            );
            this.removeErrorComponent();
            this.props.disableAddPlanButton();
            return;
        }

        this.setError(err.message);

        this.removeErrorComponent();
        this.props.disableAddPlanButton();
    }

    private onBillingPlanSaved(billingPlan: BillingPlan) {
        this.setState(
            {
                billingPlan,
                hasChanges: false,
                viewState: ViewState.Ready,
                hasError: false,
                errorMessage: ''
            });
    }

    private onBillingPlanSaveError(err: any) {
        this.setState({ viewState: ViewState.Ready });
        this.setError(err.message);
    }

    private async applyChanges(): Promise<void> {
        this.setState({ viewState: ViewState.Saving });
        this.billingPlanViewModel.applyChanges();
    }

    private addNewPlanPackage(): void {
        this.billingPlanViewModel.onNewPlanPackageAdded();
    }

    private removePlanPackage(planPackId: string): void {
        this.billingPlanViewModel.onPlanPackageRemoved(planPackId);
    }

    private onPlanRemoved(planPackId: string, dimension: Plan.DimensionEnum): void {
        this.billingPlanViewModel.onPlanRemoved(planPackId, dimension);
    }

    private changePlanPackStorageOwner(planPackId: string, criterionIdx: number, storageOwner: string): void {
        this.billingPlanViewModel.onPlanPackStorageOwnerChanged(planPackId, criterionIdx, storageOwner);
    }

    private changePlanPackStorageProvider(planPackId: string, criterionIdx: number, storageProvider: string): void {
        this.billingPlanViewModel.onPlanPackStorageProviderChanged(planPackId, criterionIdx, storageProvider);
    }

    private changePlanPackSubscriptionType(planPackId: string, criterionIdx: number, subscriptionType: string): void {
        this.billingPlanViewModel.onPlanPackSubscriptionTypeChanged(planPackId, criterionIdx, subscriptionType);
    }

    private addProvider(planPackId: string): void {
        this.billingPlanViewModel.addProvider(planPackId);
    }

    private removeProvider(planPackId: string, criterionIdx: number): void {
        this.billingPlanViewModel.removeProvider(planPackId, criterionIdx);
    }

    private changeProvider(planPackId: string, criterionIdx: number, newProvider: Providers): void {
        this.billingPlanViewModel.changeProvider(planPackId, criterionIdx, newProvider);
    }

    private onPlanTierModified(
        newValue: string,
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tierIdx: number,
        tierProp: TierProp
    ) {
        this.billingPlanViewModel.onPlanTierModified(newValue, planPackId, planDimension, tierIdx, tierProp);
    }

    private onPlanDimensionChanged(
        oldDimension: Plan.DimensionEnum,
        newDimension: Plan.DimensionEnum,
        planPackId: string
    ): void {
        this.billingPlanViewModel.onPlanDimensionChanged(oldDimension, newDimension, planPackId);
    }

    private onPlanTierAdded(planPackId: string, planDimension: Plan.DimensionEnum): void {
        this.billingPlanViewModel.onPlanTierAdded(planPackId, planDimension);
    }

    private onPlanTierRemoved(planPackId: string, planDimension: Plan.DimensionEnum, planTierIdx: number): void {
        this.billingPlanViewModel.onPlanTierRemoved(planPackId, planDimension, planTierIdx);
    }

    private removeErrorComponent(): void {
        setTimeout(
            () => {
                this.setState({ hasError: false, errorMessage: '' });
            },
            5000);
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/billing/Billing.tsx